/**
 * Kit Receive Dialog
 * Interface for receiving a kit with adjustable quantities
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  CheckCircle as CompleteIcon,
} from '@mui/icons-material';
import { rawMaterialKitsAPI } from '../../services/api';

const KitReceiveDialog = ({ open, onClose, kit, organization, onSuccess }) => {
  const [formData, setFormData] = useState({
    sessionDate: new Date().toISOString().split('T')[0],
    notes: '',
    items: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sessionStatus, setSessionStatus] = useState('IN_PROGRESS');

  useEffect(() => {
    if (open && kit) {
      // Initialize with kit items, using template quantities as defaults
      const items = (kit.kitItems || []).map(kitItem => ({
        rawMaterialItemId: kitItem.rawMaterialItemId,
        kitItemId: kitItem.id,
        rawMaterialItem: kitItem.rawMaterialItem,
        quantity: kitItem.quantity, // Default to template quantity, can be adjusted
        templateQuantity: kitItem.quantity, // Keep track of template quantity
      }));

      setFormData({
        sessionDate: new Date().toISOString().split('T')[0],
        notes: '',
        items,
      });
      setSessionId(null);
      setSessionStatus('IN_PROGRESS');
      setError('');
    }
  }, [open, kit]);

  const handleQuantityChange = (index, quantity) => {
    if (quantity < 0) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, quantity: Math.max(0, parseInt(quantity) || 0) } : item
      ),
    }));
  };

  const handleSave = async () => {
    if (formData.items.length === 0) {
      setError('Session must contain at least one item');
      return;
    }

    const itemsWithQuantity = formData.items.filter(item => item.quantity > 0);
    if (itemsWithQuantity.length === 0) {
      setError('At least one item must have a quantity greater than 0');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        sessionDate: formData.sessionDate,
        notes: formData.notes.trim() || null,
        items: itemsWithQuantity.map(item => ({
          rawMaterialItemId: item.rawMaterialItemId,
          kitItemId: item.kitItemId || null,
          quantity: item.quantity,
        })),
      };

      let response;
      if (sessionId) {
        // Update existing session
        response = await rawMaterialKitsAPI.updateReceivingSession(sessionId, {
          notes: payload.notes,
          items: payload.items.map(item => ({
            rawMaterialItemId: item.rawMaterialItemId,
            quantity: item.quantity,
          })),
        });
      } else {
        // Create new session
        response = await rawMaterialKitsAPI.createReceivingSession(kit.id, payload);
        if (response.success && response.data) {
          setSessionId(response.data.id);
          setSessionStatus(response.data.status);
        }
      }

      if (response.success) {
        onSuccess?.(response.data);
        if (!sessionId) {
          // Show success message but keep dialog open for further editing
          setSessionId(response.data.id);
        }
      } else {
        setError(response.message || 'Failed to save receiving session');
      }
    } catch (err) {
      console.error('Failed to save receiving session:', err);
      setError(err.message || 'Failed to save receiving session');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!sessionId) {
      setError('Please save the session first before completing');
      return;
    }

    if (!window.confirm('Complete this receiving session? You will be able to create individual receiving records for each item.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await rawMaterialKitsAPI.completeReceivingSession(sessionId);
      if (response.success) {
        setSessionStatus('COMPLETED');
        onSuccess?.(response.data);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(response.message || 'Failed to complete receiving session');
      }
    } catch (err) {
      console.error('Failed to complete receiving session:', err);
      setError(err.message || 'Failed to complete receiving session');
    } finally {
      setLoading(false);
    }
  };

  const totalItems = formData.items.filter(item => item.quantity > 0).length;
  const hasChanges = formData.items.some(item => item.quantity !== item.templateQuantity);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: '#039582', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Receive Kit: {kit?.name}</Typography>
          {kit?.description && (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
              {kit.description}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {sessionStatus === 'COMPLETED' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Receiving session completed successfully! You can now create individual receiving records for each item.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Session Date"
              type="date"
              value={formData.sessionDate}
              onChange={(e) => setFormData(prev => ({ ...prev, sessionDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
              disabled={!!sessionId}
            />
            <Box sx={{ flex: 1 }} />
            {sessionStatus && (
              <Chip
                label={sessionStatus === 'COMPLETED' ? 'Completed' : 'In Progress'}
                color={sessionStatus === 'COMPLETED' ? 'success' : 'warning'}
                sx={{ height: 40 }}
              />
            )}
          </Box>

          <TextField
            fullWidth
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            multiline
            rows={2}
            placeholder="Add any notes about this receiving session..."
          />

          <Divider />

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Kit Items ({totalItems} items with quantity &gt; 0)
              </Typography>
              {hasChanges && (
                <Chip
                  label="Quantities adjusted from template"
                  color="info"
                  size="small"
                />
              )}
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Adjust quantities as needed for this receiving session. Changes only affect this session, not the kit template.
            </Alert>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell><strong>Item Description</strong></TableCell>
                    <TableCell align="center" width={150}><strong>Template Qty</strong></TableCell>
                    <TableCell align="center" width={150}><strong>Session Qty *</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => {
                    const isChanged = item.quantity !== item.templateQuantity;
                    return (
                      <TableRow
                        key={index}
                        sx={{
                          bgcolor: isChanged ? '#fef3c7' : 'transparent',
                          '&:hover': { bgcolor: isChanged ? '#fde68a' : '#f8fafc' },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {item.rawMaterialItem?.itemDescription || 'Loading...'}
                          </Typography>
                          {item.rawMaterialItem?.storageRequirements && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Storage: {item.rawMaterialItem.storageRequirements}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.templateQuantity}
                            size="small"
                            variant="outlined"
                            sx={{ bgcolor: '#f3f4f6' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                            inputProps={{ min: 0 }}
                            size="small"
                            sx={{ width: 100 }}
                            error={item.quantity === 0 && formData.items.filter(i => i.quantity > 0).length === 0}
                          />
                          {isChanged && (
                            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                              {item.quantity > item.templateQuantity ? '+' : ''}
                              {item.quantity - item.templateQuantity}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {sessionId ? 'Close' : 'Cancel'}
        </Button>
        {sessionStatus !== 'COMPLETED' && (
          <>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading || totalItems === 0}
              sx={{ borderColor: '#039582', color: '#039582' }}
            >
              {loading ? <CircularProgress size={20} /> : sessionId ? 'Update Session' : 'Save Session'}
            </Button>
            {sessionId && (
              <Button
                variant="contained"
                startIcon={<CompleteIcon />}
                onClick={handleComplete}
                disabled={loading || totalItems === 0}
                sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Complete Session'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default KitReceiveDialog;
